/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Clock, FileText } from "lucide-react";
import { documentsApi } from "@/lib/api";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 20; // ~40s

/**
 * Confirmation page shown after Stripe returns from the acompte checkout flow.
 * Polls the backend to confirm payment and displays the resulting facture d'acompte reference.
 */
export default function AcompteConfirmation() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null, error: null });
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", error: "Session Stripe manquante." });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      pollsRef.current += 1;
      try {
        const data = await documentsApi.depositStatus(sessionId);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
          return;
        }
        if (data.status === "expired" || data.payment_status === "expired") {
          setState({ status: "expired", data });
          return;
        }
        if (pollsRef.current >= MAX_POLLS) {
          setState({ status: "timeout", data });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        const msg = e?.response?.data?.detail || "Erreur de vérification du paiement.";
        setState({ status: "error", error: msg });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto" data-testid="acompte-confirmation">
      <header className="mb-8">
        <p className="eyebrow">Paiement de l'acompte</p>
        <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">
          {state.status === "paid" ? (
            <>Acompte <span className="italic gold-text">bien reçu.</span></>
          ) : state.status === "polling" ? (
            <>Confirmation <span className="italic gold-text">en cours…</span></>
          ) : (
            <>Statut <span className="italic gold-text">du paiement.</span></>
          )}
        </h1>
      </header>

      {state.status === "polling" && (
        <div className="card-luxe p-10 text-center" data-testid="acompte-polling">
          <Loader2 size={44} strokeWidth={1.2} className="text-[#DCB457] mx-auto mb-4 animate-spin" />
          <p className="text-white/70">
            Nous confirmons votre paiement auprès de Stripe. Merci de patienter quelques secondes…
          </p>
        </div>
      )}

      {state.status === "paid" && (
        <div className="card-luxe p-10" data-testid="acompte-paid">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center bg-emerald-500/15 border border-emerald-400/40 rounded-sm shrink-0">
              <CheckCircle2 size={22} strokeWidth={1.4} className="text-emerald-300" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-300 mb-1">Paiement confirmé</p>
              <p className="text-white/75 leading-relaxed">
                Votre acompte de <strong className="text-[#DCB457]">
                  {Number(state.data?.amount || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €
                </strong> a bien été reçu. Votre date est désormais définitivement réservée.
              </p>
            </div>
          </div>
          {state.data?.acompte_invoice_number && (
            <div className="bg-[#DCB457]/6 border border-[#DCB457]/25 p-5 mb-6">
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#DCB457] mb-1">Facture d'acompte</p>
              <p className="font-mono text-lg text-white">{state.data.acompte_invoice_number}</p>
              <p className="text-xs text-white/55 mt-2">
                Elle vient de vous être envoyée par email. Vous la retrouverez aussi dans vos documents.
              </p>
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            <Link
              to="/espace-client/documents"
              data-testid="acompte-back-documents"
              className="btn-gold inline-flex items-center gap-2"
            >
              <FileText size={14} strokeWidth={1.5} /> Voir mes documents
            </Link>
            <Link
              to="/espace-client"
              data-testid="acompte-back-dashboard"
              className="btn-ghost-gold"
            >
              Retour au tableau de bord
            </Link>
          </div>
        </div>
      )}

      {state.status === "timeout" && (
        <div className="card-luxe p-10" data-testid="acompte-timeout">
          <Clock size={40} strokeWidth={1.2} className="text-[#DCB457] mb-4" />
          <p className="text-white/75 leading-relaxed mb-4">
            Le paiement est encore en cours de confirmation côté Stripe. Cela peut prendre quelques minutes.
            Rechargez cette page ou consultez vos documents dans quelques instants.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => window.location.reload()}
              data-testid="acompte-refresh"
              className="btn-gold"
            >
              Rafraîchir
            </button>
            <Link to="/espace-client/documents" className="btn-ghost-gold">
              Voir mes documents
            </Link>
          </div>
        </div>
      )}

      {(state.status === "expired" || state.status === "error") && (
        <div className="card-luxe p-10" data-testid="acompte-error">
          <XCircle size={40} strokeWidth={1.2} className="text-red-400 mb-4" />
          <p className="text-white/75 leading-relaxed mb-4">
            {state.status === "expired"
              ? "La session de paiement a expiré. Vous pouvez relancer le paiement depuis vos documents."
              : (state.error || "Nous n'avons pas pu confirmer votre paiement. Contactez-nous si vous avez été débité.")}
          </p>
          <Link to="/espace-client/documents" className="btn-gold">Retour à mes documents</Link>
        </div>
      )}
    </div>
  );
}
